import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SubSkills from "../Subskills";
import SkillData from "../../Components/data/home-page-static-data.json";
import { renderCustomIcon } from "../GlobalFunctions";
import IndustrialUsageChart from "../../IndustrialUsageChart";
import PopularityChart from "../../PopularityChartV2";
import LearnAndEarnSuggestion from "../common/Learn-and-earn-suggestion";
import SuggestedRoles from "../common/SuggestedRoles";
import RolesWithResponsibilities from "../common/RolesWithResponsibilities";
import Courses from "../common/Courses";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

function CareerInsightsSection() {
  const theme = useTheme();
  const isLargeDevice = useMediaQuery(theme.breakpoints.up("lg")); // Only show on large devices and up
  const sectionRef = useRef(null);
  const phoneRef = useRef(null);
  const contentRef = useRef(null);
  const stickyContainerRef = useRef(null);
  const [currentSection, setCurrentSection] = useState(0);

  // Convert the data to match SubSkills component format

  // Single section for Sub Skills
  const sections = [
    {
      id: "sub-skills",
      title: "Sub Skills",
      subtitle: "Sub-Skills & Related Skills",
      description:
        "Enter a skill to instantly discover its sub-skills and related skills, helping you understand your expertise map and growth opportunities.",
      image: "../../assets/images/iphone-mock-up/subskills.png",
      sectionHeader: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row !important",
            gap: 1,
            mb: 1,
          }}
        >
          {renderCustomIcon("sub-skills", 40)}
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: "Poppins, sans-serif",
              textAlign: "center",
              fontWeight: 700,
              color: "#000088",
            }}
          >
            Sub skills
          </Typography>
        </Box>
      ),
      sectionContent: (
        <Box sx={{ px: 2 }}>
          <SubSkills
            subSkills={SkillData?.subSkills}
            chatGptButton={false}
            loop={false}
          />
        </Box>
      ),
    },
    {
      id: "enterprise-adoption",
      title: "Enterprise Adoption",
      subtitle: "Enterprise Adaptation Percentage",
      description:
        "See how your skills align with enterprise-level requirements, with a percentage-based adaptation score for accurate career insights",
      image: "../../assets/images/iphone-mock-up/enterprise-adoption.png",
      sectionHeader: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 1,
          }}
        >
          {renderCustomIcon("enterprice", 40)}
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: "Poppins, sans-serif",
              textAlign: "center",
              fontWeight: 700,
              color: "#000088",
            }}
          >
            Enterprise Adoption
          </Typography>
        </Box>
      ),
      sectionContent: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <IndustrialUsageChart
            value={parseInt(SkillData?.percentageUsage || 10)}
            color={"#000088"}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 1,
              flexDirection: "column !important",
            }}
          >
            <Typography
              color="primary"
              variant="h6"
              sx={{
                fontFamily: "Poppins, sans-serif",
                textAlign: "center",
                fontWeight: 700,
                color: "#000088",
              }}
            >
              Popularity
            </Typography>
            <PopularityChart
              value={parseInt(SkillData?.popularityRating) * 10 || 10}
              color={"#000088"}
            />
          </Box>
        </Box>
      ),
    },
    {
      id: "learn-earn",
      title: "Learn & Earn",
      subtitle: "Learn & Earn Value Recommendation",
      description:
        "Get insights on which skills to learn, how much they can increase your earning potential, and the percentage of value they can add to your career.",
      image:
        "../../assets/images/iphone-mock-up/learn-and-earn-recommendation.png",
      sectionHeader: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 1,
            px: 10,
          }}
        >
          {renderCustomIcon("learn-earn", 40)}
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: "Poppins, sans-serif",
              textAlign: "center",
              fontWeight: 700,
              color: "#000088",
            }}
          >
            Latest Technologies
          </Typography>
        </Box>
      ),
      sectionContent: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 1,
            px: 4,
            py: 4,
          }}
        >
          <LearnAndEarnSuggestion chatGPTData={SkillData} />
        </Box>
      ),
    },
    {
      id: "suggested-roles",
      title: "Suggested Roles",
      subtitle: "Suggested Career Roles",
      description:
        "Explore the best career roles that match your skillset, with recommendations tailored to your strengths and professional goals.",
      image: "../../assets/images/iphone-mock-up/suggested-role.png",
      sectionHeader: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row !important",
            gap: 1,
            mb: 1,
          }}
        >
          {renderCustomIcon("suggested-roles", 40)}
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: "Poppins, sans-serif",
              textAlign: "center",
              fontWeight: 700,
              color: "#000088",
            }}
          >
            Suggested Roles
          </Typography>
        </Box>
      ),
      sectionContent: (
        <Box sx={{ gap: 1, mb: 1 }}>
          <SuggestedRoles
            suggestedRoles={
              SkillData?.suggestedRoles?.suggestedRolesWithSalaryIncrease
            }
          />
        </Box>
      ),
    },
    {
      id: "roles-with-responsibilities",
      title: "Roles with Responsibilities",
      subtitle: "Roles & Responsibilities Insights",
      description:
        "Understand the key responsibilities and related roles based on your primary career role, helping you expand your professional scope.",
      image:
        "../../assets/images/iphone-mock-up/other-roles-with-responisibilites.png",
      sectionHeader: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row !important",
            gap: 1,
            mb: 1,
          }}
        >
          {renderCustomIcon("other-roles-with-responsibilities", 40)}
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: "Poppins, sans-serif",
              textAlign: "center",
              fontWeight: 700,
              color: "#000088",
            }}
          >
            Roles with Responsibilities
          </Typography>
        </Box>
      ),
      sectionContent: (
        <Box>
          <RolesWithResponsibilities
            otherRoles={
              SkillData?.suggestedRoles?.otherRolesWithResponsibilities
            }
          />
        </Box>
      ),
    },
    {
      id: "courses",
      title: "Courses",
      subtitle: "Skill-Based Course Recommendations",
      description:
        "Discover top courses for your skills from trusted platforms like YouTube, Google Search, Google Gemini, and ChatGPT to enhance your expertise.",
      image: "../../assets/images/iphone-mock-up/courses.png",
      sectionHeader: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row !important",
            gap: 1,
            mb: 1,
          }}
        >
          {renderCustomIcon("courses", 40)}
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: "Poppins, sans-serif",
              textAlign: "center",
              fontWeight: 700,
              color: "#000088",
            }}
          >
            Courses
          </Typography>
        </Box>
      ),
      sectionContent: (
        <Box>
          <Courses checkSkill={SkillData?.skillName || "Java"} />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Set up sticky container
      const stickyContainer = stickyContainerRef.current;
      const scrollTriggersContainer = document.querySelector(
        ".scroll-triggers-container"
      );

      if (stickyContainer) {
        gsap.set(stickyContainer, {
          position: "sticky",
          marginTop: 10,
          top: 145,
          height: "72vh",
          zIndex: 10,
          display: isLargeDevice ? "block" : "none",
        });
      }

      // Set up scroll triggers container
      if (scrollTriggersContainer) {
        gsap.set(scrollTriggersContainer, {
          position: "relative",
          zIndex: 1,
        });
      }

      // Set initial content immediately
      if (sections.length > 0) {
        updateContent(sections[0]);
        setCurrentSection(0);
      }

      // Create scroll-triggered content changes with a small delay to ensure DOM is ready
      setTimeout(() => {
        sections.forEach((sectionData, index) => {
          const triggerElement = document.getElementById(sectionData.id);
          if (triggerElement) {
            ScrollTrigger.create({
              trigger: triggerElement,
              start: "top center",
              end: "bottom center",
              onEnter: () => {
                updateContent(sectionData);
                setCurrentSection(index);
              },
              onEnterBack: () => {
                updateContent(sectionData);
                setCurrentSection(index);
              },
              markers: false, // Set to true for debugging
            });
          }
        });

        // Create a trigger for the initial section to ensure it's active when page loads
        if (sections.length > 0) {
          ScrollTrigger.create({
            trigger: document.getElementById(sections[0].id),
            start: "top bottom",
            end: "bottom top",
            onEnter: () => {
              updateContent(sections[0]);
              setCurrentSection(0);
            },
            immediateRender: true,
          });
        }

        // Refresh ScrollTrigger to ensure proper positioning
        ScrollTrigger.refresh();
      }, 100);
    }, sectionRef);

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  // Ensure first section is immediately visible on mount
  useEffect(() => {
    if (sections.length > 0) {
      setCurrentSection(0);
      updateContent(sections[0]);
    }
  }, []);

  const updateContent = (sectionData) => {
    if (contentRef.current) {
      const contentElement = contentRef.current;

      // Animate out current content
      gsap.to(contentElement, {
        opacity: 0,
        x: -50,
        duration: 0.3,
        onComplete: () => {
          // Update content
          contentElement.innerHTML = `
            <div style={{
              p: 4,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center'
            }}>
              
              <h1 style={{
                lineHeight: 1.8,
                color: '#e0e0e0',                
                maxWidth: '500px',
                fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                fontSize: { xs: '2rem', md: '3.5rem' },
              }}>
                ${sectionData.subtitle}
              </h1>
              <p style={{
                lineHeight: 1.8,
                color: '#e0e0e0',
                fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                fontSize: '1.25,
                // maxWidth: '591px'
              }}>
                ${sectionData.description}
              </p>
            </div>
          `;

          // Animate in new content
          gsap.to(contentElement, {
            opacity: 1,
            x: 0,
            duration: 0.5,
            ease: "power2.out",
          });
        },
      });
    }
  };

  return (
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      {/* Sticky Container - Apple-style viewport-content */}
      <Box
        ref={stickyContainerRef}
        maxWidth="xl"
        className="viewport-content"
        sx={{
          background:
            "linear-gradient(180deg,rgb(87 87 87 / 30%) 0%,rgba(255, 255, 255, 0) 100%)",
          color: "white",
          alignItems: "center",
          justifyContent: "flex-end",
          overflow: "hidden",
          borderRadius: "30px",
          p: 3,
          mx: { md: "100px", xl: "213px" },
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: isLargeDevice ? "block" : "none", // Only show on large devices (laptop to desktop)
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: "absolute",
            top: 80,
            left: 50,
            right: 0,
            zIndex: 1000,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              textAlign: "left",
              fontFamily: "'SF Pro Display', 'Inter', sans-serif",
              fontSize: { xs: "2rem", md: "3.5rem" },
              fontWeight: 700,
              color: "#fff",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
            }}
          >
            What You'll Get
          </Typography>
        </Box>

        <Container maxWidth="xl">
          {/* Apple-style features container */}
          <Box
            className="styles-features-container-base"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            {/* Content Section */}
            <Box
              ref={contentRef}
              className="feature"
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pr: { md: 4 },
              }}
            ></Box>

            {/* Phone Mockup - Device Container */}
            <Box
              className="device-container"
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                pl: { md: 8, lg: 12 },
              }}
            >
              <Box
                ref={phoneRef}
                className="device"
                sx={{
                  width: { xs: "300px", md: "351px", lg: "351px" },
                  height: { xs: "600px", md: "879px", lg: "717px" },
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Phone Screen Content */}
                <Box
                  className="phone-screen"
                  sx={{
                    width: "100%",
                    height: "717px",
                    borderRadius: "40px",
                    padding: 3,
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    justifyContent: "center",
                    backgroundImage:
                      "url(/assets/images/iphone-mock-up/iphone-mock-up.svg)",
                    // backgroundSize: 'cover',
                    // backgroundPosition: 'center',
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  {sections[currentSection]?.sectionHeader}

                  {sections[currentSection]?.sectionContent}
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Desktop Scroll Triggers - Apple-style feature wrappers */}
      <Box
        className="scroll-triggers-container"
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          paddingTop: "20px",
          display: isLargeDevice ? "block" : "none", // Only show on large devices (laptop to desktop)
        }}
      >
        {sections.map((section, index) => (
          <Box
            key={section.id}
            id={section.id}
            className="feature-wrapper"
            sx={{
              height: "100vh",
              width: "100%",
              position: "relative",
              marginBottom: index === sections.length - 1 ? "100px" : "0",
            }}
          />
        ))}
      </Box>

      {/* Mobile/Tablet Sections */}
      <Box
        sx={{
          display: isLargeDevice ? "none" : "flex", // Show on all devices except large ones
          position: "relative",
          zIndex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          p: 2,
        }}
      >
        <Box sx={{ my: 3 }}>
          <Typography
            variant="h1"
            sx={{
              textAlign: "center",
              fontSize: { xs: "2rem", md: "3.5rem" },
              fontFamily: "'SF Pro Display', 'Inter', sans-serif",
              fontWeight: 700,
              color: "#fff",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
            }}
          >
            What You'll Get
          </Typography>
        </Box>
        {sections.map((section, index) => (
          <Box
            key={section.id}
            sx={{
              minHeight: "100%",
              width: "100%",
              position: "relative",
              padding: { xs: 2, sm: 4 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              background:
                "linear-gradient(180deg,rgb(87 87 87 / 30%) 0%,rgba(255, 255, 255, 0) 100%)",
              marginBottom: index === sections.length - 1 ? "50px" : "0",
            }}
          >
            {/* Section Content */}
            <Box
              sx={{
                textAlign: "center",
                maxWidth: { xs: "100%", sm: "400px" },
                padding: { xs: 2, sm: 3 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                  marginBottom: 2,
                  fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                }}
              >
                {section.subtitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#e0e0e0",
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  lineHeight: 1.6,
                  opacity: 0.9,
                  fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                }}
              >
                {section.description}
              </Typography>
            </Box>

            {/* Section Image */}
            <Box
              sx={{
                width: { xs: "280px", sm: "320px" },
                height: { xs: "560px", sm: "640px" },
                borderRadius: { xs: "25px", sm: "30px" },
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
              }}
            >
              <img
                src={section.image}
                alt={section.title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default CareerInsightsSection;
